import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Marquee from "../components/Marquee";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import JourneyCard from "../components/JourneyCard";
import Seo from "../lib/Seo";
import { itineraries } from "../data/itineraries";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const featured = ["the-regenerative-safari", "iconic-kenya-safari", "absolute-tanzania", "gorillas-and-the-nile"]
  .map((s) => itineraries.find((i) => i.slug === s))
  .filter(Boolean);

const chapters = [
  {
    num: "01",
    title: "Kesho",
    trans: "Tomorrow",
    body:
      "Every journey we design is built for the Africa our children will inherit. Regenerative by principle, quiet by practice.",
  },
  {
    num: "02",
    title: "Ustadi",
    trans: "Craft",
    body:
      "A private planner, one guide across the entire journey, aircraft where roads do not serve. The craft is in the invisible details.",
  },
  {
    num: "03",
    title: "Njia",
    trans: "The Path",
    body:
      "We do not sell places. We compose a path — through people, land, and hours — that could only ever be yours.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const heroTint = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-50%"]);

  // Prevent SSR mismatch — this runs client only
  useEffect(() => {}, []);

  return (
    <>
      <Seo
        title="Njia Safaris — Ultra-Luxury African Safaris in Kenya, Tanzania & Uganda"
        description="Njia Safaris designs ultra-luxury private safaris across Kenya, Tanzania and Uganda. A carbon-positive 2026 Signature Journey, ten signature itineraries, one path — Africa's soul."
        path="/"
      />
      <Nav />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative h-[100svh] overflow-hidden section-dark"
        data-testid="home-hero"
      >
        <motion.div
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0"
        >
          <img
            src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2400&q=85"
            alt="Wildebeest calving on the green savannah"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroTint }}
          className="absolute inset-0 bg-[color:var(--ink)]"
        />
        <div className="grain-overlay" />

        {/* Hero content */}
        <motion.div
          style={{ y: titleY }}
          className="relative z-10 h-full container-x flex flex-col justify-end pb-16 md:pb-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-16 bg-[color:var(--gold)]" />
            <span className="eyebrow eyebrow-invert">Njia — the path</span>
          </div>
          <RevealText
            as="h1"
            className="h-display text-[14vw] md:text-[10vw] text-[color:var(--ivory)]"
            lines={["Your path", <em key="e" className="italic text-[color:var(--gold-3)]">to Africa&rsquo;s soul.</em>]}
            stagger={120}
          />
          <div className="mt-10 grid md:grid-cols-2 gap-8 md:gap-16 items-end">
            <p className="max-w-lg text-[color:var(--sand)] text-lg leading-relaxed">
              Ten signature journeys across Kenya, Tanzania and Uganda. Private guides. Small camps.
              A quieter, more considered safari — designed for those who arrive already knowing what they want.
            </p>
            <div className="flex items-center gap-4 md:justify-end">
              <Link to="/journeys" className="btn-gold" data-testid="hero-cta-journeys">
                Explore Travel Options
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
              <Link to="/signature-journey" className="btn-ghost btn-ghost-invert" data-testid="hero-cta-signature">
                2026 Signature
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-6 right-6 md:right-14 z-10 flex items-center gap-3 text-[color:var(--sand)]"
        >
          <span className="mono text-[10px] uppercase tracking-[0.28em]">Scroll</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          >
            <ArrowDown size={14} strokeWidth={1.4} />
          </motion.span>
        </motion.div>
      </section>

      {/* THE INSPIRATION — MENTORED BY JEET AMAN */}
      <section className="section-dark relative overflow-hidden" data-testid="home-inspiration">
        {/* Warm textural backdrop — subtle warm gradient + grain, no literal workspace image */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 15% 20%, rgba(201,168,76,0.55), transparent 60%), radial-gradient(50% 45% at 85% 80%, rgba(31,58,46,0.7), transparent 65%)",
          }}
        />
        <div className="grain-overlay" />

        <div className="relative container-x section-y">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            {/* Text column */}
            <div className="md:col-span-6">
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-16 bg-[color:var(--gold)]" />
                <span className="eyebrow eyebrow-invert">The inspiration</span>
              </div>
              <RevealText
                as="h2"
                className="h-display text-5xl md:text-7xl text-[color:var(--ivory)] leading-[0.95]"
                lines={["Mentored by", <em key="e" className="italic text-[color:var(--gold-3)]">Jeet Aman.</em>]}
                stagger={110}
              />
              <p className="mt-10 text-lg md:text-xl leading-relaxed text-[color:var(--sand)] max-w-xl">
                Njia Safaris is orchestrated through the creative vision and mentorship of master wildlife
                photographer Jeet Aman. Every journey we design follows the <em className="italic font-italic-display text-[color:var(--gold-3)]">njia</em> — the path —
                inspired by his deep connection to the wild soul of Africa and India.
              </p>

              <div className="hairline hairline-invert my-10" />

              <div className="grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <p className="chapter-num" style={{ fontSize: "3rem" }}>01</p>
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mt-1">Vision</p>
                </div>
                <div>
                  <p className="chapter-num" style={{ fontSize: "3rem" }}>02</p>
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mt-1">Mentorship</p>
                </div>
                <div>
                  <p className="chapter-num" style={{ fontSize: "3rem" }}>03</p>
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mt-1">The path</p>
                </div>
              </div>
            </div>

            {/* Anchoring image column */}
            <Reveal className="md:col-span-6" delay={0.15}>
              <div className="relative">
                <div className="img-frame aspect-[4/5] shadow-editorial">
                  <img
                    src="https://jeetamanphotography.com/wp-content/uploads/2025/03/AJ-P3-Best-in-Sunlight-DSC_6984-2.jpg"
                    alt="Tiger in sunlight — a frame by Jeet Aman"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 border px-5 py-3 backdrop-blur-sm bg-[color:var(--ink)]/80"
                  style={{ borderColor: "var(--gold)" }}
                >
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)]">
                    A frame by Jeet Aman
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED GALLERY — FRAMES FROM THE LENS */}
      <section className="section-forest section-y" data-testid="home-lens-gallery">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-4">
              <p className="eyebrow eyebrow-invert mb-4">Featured · The lens</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl text-[color:var(--ivory)]"
                lines={["Frames from", <em key="e" className="italic text-[color:var(--gold-3)]">the field.</em>]}
              />
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-lg leading-relaxed text-[color:var(--sand)] max-w-xl">
                A brief selection from Jeet Aman&rsquo;s work across the wild — tigers of the subcontinent,
                elephants of the plains, and polar wildlife of the white north. The full gallery follows.
              </p>
            </div>
          </div>

          {/* 3-image grid — editorial with staggered heights */}
          <div className="grid grid-cols-12 gap-6 md:gap-8">
            <Reveal className="col-span-12 md:col-span-4" delay={0}>
              <div className="img-frame aspect-[3/4]">
                <img
                  src="https://jeetamanphotography.com/wp-content/uploads/2025/03/AJ-P3-Best-in-Sunlight-DSC_6984-2.jpg"
                  alt="Tiger prowling — by Jeet Aman"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--sand)]">
                01 · Tiger · Ranthambhore
              </p>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-4 md:mt-16" delay={0.1}>
              <div className="img-frame aspect-[3/4]">
                <img
                  src="https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?auto=format&fit=crop&w=1600&q=85"
                  alt="Elephant family on the plains"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--sand)]">
                02 · Elephant · The plains
              </p>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-4" delay={0.2}>
              <div className="img-frame aspect-[3/4]">
                <img
                  src="https://jeetamanphotography.com/wp-content/uploads/2025/04/Jeet-Polar-Bear-with-Cubs.jpg"
                  alt="Polar bear with cubs — by Jeet Aman"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--sand)]">
                03 · Polar bear with cubs · Wapusk
              </p>
            </Reveal>
          </div>

          {/* CTA */}
          <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <p className="font-italic-display text-xl md:text-2xl text-[color:var(--gold-3)] max-w-xl leading-snug">
              &ldquo;Every frame is a story of patience, respect, and the raw beauty of the wild.&rdquo;
              <span className="block mt-2 mono text-[10px] uppercase tracking-[0.28em] not-italic text-[color:var(--sand)]">
                — Jeet Aman
              </span>
            </p>
            <Link
              to="/lens-of-jeet-aman"
              className="btn-gold"
              data-testid="home-lens-cta"
            >
              Explore the Lens of Jeet Aman
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="section-y container-x" data-testid="home-manifesto">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <p className="eyebrow mb-6">A quieter way to see Africa</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-6xl"
              lines={["Not more,", <em key="less" className="italic text-[color:var(--forest)]">More considered.</em>]}
            />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-xl md:text-2xl font-display leading-snug mb-10 max-w-2xl">
              Njia is the Swahili word for <em className="italic text-[color:var(--forest)]">path</em>.
              We compose paths — not packages — through the last quiet corners of East Africa.
            </p>
            <div className="hairline" />
            {chapters.map((c) => (
              <Reveal key={c.num} y={30}>
                <div className="grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-[color:var(--line)]">
                  <div className="col-span-3 md:col-span-2">
                    <span className="chapter-num">{c.num}</span>
                  </div>
                  <div className="col-span-9 md:col-span-4">
                    <h3 className="font-display text-3xl md:text-4xl leading-none">
                      {c.title}
                    </h3>
                    <p className="mt-2 font-italic-display text-[color:var(--forest)] text-lg">
                      {c.trans}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/85 max-w-md">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED IMAGE PAIR — editorial spotlight */}
      <section className="section-y container-x" data-testid="home-editorial">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
          <Reveal className="md:col-span-7">
            <div className="img-frame aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=2000&q=85"
                alt="Elephants on the savannah"
              />
            </div>
          </Reveal>
          <Reveal className="md:col-span-4 md:col-start-9 md:-mt-32" delay={0.15}>
            <div className="img-frame aspect-[3/4]">
              <img
                src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1400&q=85"
                alt="African plains at sunset"
              />
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-14 max-w-2xl" delay={0.2}>
          <p className="font-italic-display text-2xl md:text-3xl leading-snug text-[color:var(--forest)]">
            &ldquo;The safari does not begin at the airstrip. It begins the moment
            you decide the version of yourself you want to be here.&rdquo;
          </p>
          <p className="mt-4 mono text-[11px] uppercase tracking-widest">— Njia field journal, entry no.14</p>
        </Reveal>
      </section>

      {/* FEATURED JOURNEYS */}
      <section className="section-y" data-testid="home-featured">
        <div className="container-x">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="eyebrow mb-4">Selected journeys</p>
              <RevealText
                as="h2"
                className="h-display text-5xl md:text-6xl"
                lines={["A selection.", <em key="e" className="italic text-[color:var(--forest)]">Forty-two more await on the journeys page.</em>]}
              />
            </div>
            <Link to="/journeys" className="link-under" data-testid="home-see-all">
              See all ten journeys →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16 md:gap-y-24">
            {featured.map((j, i) => (
              <div key={j.slug} className={i % 2 === 1 ? "md:mt-24" : ""}>
                <JourneyCard journey={j} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="section-forest py-16" data-testid="home-marquee">
        <Marquee
          items={[
            "Serengeti",
            "Masai Mara",
            "Ngorongoro",
            "Amboseli",
            "Bwindi",
            "Laikipia",
            "Samburu",
            "Ruaha",
            "Katavi",
            "Zanzibar",
          ]}
        />
      </section>

      {/* TWO PATHS — CHOOSE YOURS */}
      <section className="section-sand section-y" data-testid="home-two-paths">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">Choose your path</p>
              <RevealText
                as="h2"
                className="h-display text-5xl md:text-6xl leading-[0.95]"
                lines={["Two paths.", <em key="e" className="italic text-[color:var(--forest)]">One Africa.</em>]}
              />
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="font-italic-display text-xl md:text-2xl text-[color:var(--forest)] leading-snug max-w-xl">
                Every guest arrives with a different appetite for the wild. We compose journeys around two of them.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Path 01 — Families & Enthusiasts */}
            <Reveal delay={0.05}>
              <Link
                to="/experiences"
                className="group block relative border border-[color:var(--ink)]/15 bg-[color:var(--ivory)] p-8 md:p-12 h-full transition-all duration-500 hover:border-[color:var(--gold)] hover:shadow-editorial"
                data-testid="home-path-families"
              >
                <span className="chapter-num" style={{ fontSize: "4rem" }}>01</span>
                <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)] mt-2 mb-6">
                  4–7 nights · Short-form
                </p>
                <h3 className="font-display text-3xl md:text-4xl leading-[1.05] mb-4">
                  For Families &amp; <em className="italic text-[color:var(--forest)]">Travel Enthusiasts</em>
                </h3>
                <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/80 mb-6 max-w-md">
                  Weekend safaris, tight bush-and-beach edits, first-time Africa. Everything included, nothing to plan.
                  Composed for guests with limited leave and a serious appetite.
                </p>
                <div className="hairline mb-6" />
                <ul className="space-y-2 text-[14px] mb-8 max-w-md">
                  <li className="flex items-start gap-3">
                    <span className="text-[color:var(--gold-2)] mt-1">◆</span>
                    <span>Masai Mara Express · Nairobi Magic · Amani Safari</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[color:var(--gold-2)] mt-1">◆</span>
                    <span>Tarangire Quick · Serengeti Express · Uganda Gorilla Express</span>
                  </li>
                </ul>
                <span className="link-under inline-flex items-center gap-2 group-hover:text-[color:var(--gold-2)]">
                  See short-form journeys
                  <ArrowUpRight size={12} strokeWidth={1.8} />
                </span>
              </Link>
            </Reveal>

            {/* Path 02 — Photographers, Filmmakers & Professionals */}
            <Reveal delay={0.15}>
              <Link
                to="/journeys"
                className="group block relative border p-8 md:p-12 h-full transition-all duration-500 hover:shadow-editorial"
                style={{ background: "var(--ink)", borderColor: "var(--gold)" }}
                data-testid="home-path-professionals"
              >
                <span className="chapter-num" style={{ fontSize: "4rem", color: "var(--gold-3)" }}>02</span>
                <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mt-2 mb-6">
                  8+ nights · Long-form
                </p>
                <h3 className="font-display text-3xl md:text-4xl leading-[1.05] mb-4 text-[color:var(--ivory)]">
                  For Wildlife Photographers, <em className="italic text-[color:var(--gold-3)]">Filmmakers &amp; Professionals</em>
                </h3>
                <p className="text-[15px] leading-relaxed text-[color:var(--sand)] mb-6 max-w-md">
                  Long-form journeys — the great migration in full, remote parks, community-led conservancies.
                  Composed for repeat safari-goers, filmmakers, and serious cameras.
                </p>
                <div className="hairline hairline-invert mb-6" />
                <ul className="space-y-2 text-[14px] mb-8 max-w-md text-[color:var(--sand)]">
                  <li className="flex items-start gap-3">
                    <span className="text-[color:var(--gold-3)] mt-1">◆</span>
                    <span>The Regenerative Safari · Kenya Ndoto · Life of My Choice</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[color:var(--gold-3)] mt-1">◆</span>
                    <span>Tanzania Private Wilderness · The Birthing of the Wildebeest</span>
                  </li>
                </ul>
                <span className="link-under inline-flex items-center gap-2 text-[color:var(--gold-3)] group-hover:text-[color:var(--ivory)]">
                  See long-form journeys
                  <ArrowUpRight size={12} strokeWidth={1.8} />
                </span>
              </Link>
            </Reveal>
          </div>

          <p className="mt-14 text-center mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60">
            Not sure which fits?{" "}
            <Link to="/contact" className="underline decoration-[color:var(--gold)] underline-offset-4 hover:text-[color:var(--forest)]">
              Speak with a specialist
            </Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y container-x" data-testid="home-cta">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow mb-6">Begin planning</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-7xl"
              lines={["Tell us the version", <em key="e" className="italic text-[color:var(--forest)]">of Africa you seek.</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="max-w-md text-lg leading-relaxed mb-8">
              Every itinerary is designed privately. Share your dates and any dream in mind —
              our planners will compose a journey for you within seven days.
            </p>
            <Link to="/contact" className="btn-gold" data-testid="home-cta-contact">
              Compose My Journey
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
